import { DateTime } from "luxon";
import type { RootState } from "StoreTypes";
import { useSelector } from "react-redux";
import { BreakPoints, useMediaQuery } from "utils/animations/animationHelper";
import { queueAction } from "ui-modules/nav/navSlice";
import {
  setLocalStorageValue,
  LocalStorageItems,
} from "utils/hooks/localStorage";
import { useAppDispatch } from "utils/hooks";
import { useCallback, type CSSProperties } from "react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { GroupBase, OptionProps, StylesConfig } from "react-select";

/**
 * Widened generics so these objects assign to `StylesConfig<Option, ...>` for
 * every `Select` (default `StylesConfig` uses `unknown`, which makes style
 * callback props incompatible with typed options under strictFunctionTypes).
 */
export type AppSharedSelectStyles = StylesConfig<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any,
  boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  GroupBase<any>
>;

/** Non-standard prop some call sites pass through react-select option state. */
function isOptionDarkMode(
  state: OptionProps<unknown, boolean, GroupBase<unknown>>,
): boolean {
  return Boolean(
    (
      state as OptionProps<unknown, boolean, GroupBase<unknown>> & {
        isDarkMode?: boolean;
      }
    ).isDarkMode,
  );
}

/** Matches Tailwind `dark` on `<html>` (see App.tsx); inline select styles cannot use `dark:`. */
function isAppDarkMode(): boolean {
  if (typeof document === "undefined") return false;
  return document.documentElement.classList.contains("dark");
}

/** Disabled option row: light gray on light UI, zinc on dark UI. */
function disabledOptionRowColors(): {
  color: string;
  backgroundColor: string;
} {
  return isAppDarkMode()
    ? { color: "#a1a1aa", backgroundColor: "#27272a" }
    : { color: "#9ca3af", backgroundColor: "#f3f4f6" };
}

/** Unselected option text: black in light app, light zinc in dark app. */
function defaultOptionTextColor(
  state: OptionProps<unknown, boolean, GroupBase<unknown>>,
): string {
  if (state.isSelected) return "green";
  return isAppDarkMode() ? "#e4e4e7" : "#000000";
}

/**
 * Non-disabled option row background: mirrors `disabledOptionRowColors` by
 * using `isAppDarkMode()` for the non-legacy branches; `legacyHighlight` is
 * used when `isOptionDarkMode(state)` (react-select custom flag).
 */
function defaultOptionRowBackground(
  state: OptionProps<unknown, boolean, GroupBase<unknown>>,
  legacyHighlight: { light: string; dark: string },
): string {
  if (isOptionDarkMode(state)) {
    return isAppDarkMode() ? legacyHighlight.dark : legacyHighlight.light;
  }
  return isAppDarkMode() ? "#18181b" : "#ffffff";
}

export const customStyles: AppSharedSelectStyles = {
  option: (provided, state) => {
    const disabledStyle = state.isDisabled ? disabledOptionRowColors() : null;
    return {
      ...provided,
      color: disabledStyle
        ? disabledStyle.color
        : defaultOptionTextColor(state),
      backgroundColor: disabledStyle
        ? disabledStyle.backgroundColor
        : defaultOptionRowBackground(state, {
            light: "#e1f0ff",
            dark: "#1e3a5f",
          }),
      cursor: state.isDisabled ? "not-allowed" : "default",
      fontSize: "14px",
      fontFamily: "Poppins, ui-sans-serif, system-ui",
    };
  },
  control: (provided, state) => {
    const disabledStyle = state.isDisabled ? disabledOptionRowColors() : null;
    return {
      ...provided,
      color: disabledStyle ? disabledStyle.color : "#000000",
      cursor: disabledStyle ? "not-allowed" : "default",
      backgroundColor: disabledStyle
        ? disabledStyle.backgroundColor
        : "#e1f0ff",
      border: "1px solid #CCCCCC",
      borderRadius: 10,
      paddingTop: 4,
      paddingBottom: 4,
      paddingLeft: 10,
      paddingRight: 10,
      fontSize: "14px",
      fontFamily: "Poppins, ui-sans-serif, system-ui",
      ":valid": {
        border: "1px solid #22c55e",
      },
    };
  },
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#D9D9D9",
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    color: "transparent",
    display: "none",
  }),
  valueContainer: (provided) => ({
    ...provided,
    fontSize: "14px",
    fontFamily: "Poppins, ui-sans-serif, system-ui",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#f3f4f6",
    color: "#000000",
    fontSize: "14px",
    fontFamily: "Poppins, ui-sans-serif, system-ui",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#000000",
    fontSize: "14px",
    fontFamily: "Poppins, ui-sans-serif, system-ui",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#1f293",
    ":hover": {
      backgroundColor: "#E1341E",
      color: "white",
    },
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    padding: "10px",
    fontSize: "14px",
    fontFamily: "Poppins, ui-sans-serif, system-ui",
  }),
};

export const customStylesSmall: AppSharedSelectStyles = {
  option: (provided, state) => {
    const disabledStyle = state.isDisabled ? disabledOptionRowColors() : null;
    return {
      ...provided,
      color: disabledStyle
        ? disabledStyle.color
        : defaultOptionTextColor(state),
      backgroundColor: disabledStyle
        ? disabledStyle.backgroundColor
        : defaultOptionRowBackground(state, {
            light: "#d1d5db",
            dark: "#3f3f46",
          }),
      cursor: state.isDisabled ? "not-allowed" : "default",
      fontSize: "12px",
      fontFamily: "Poppins",
    };
  },
  control: (provided) => ({
    ...provided,
    border: "1px solid #CCCCCC",
    color: "#000000",
    borderRadius: 6,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 5,
    paddingRight: 5,
    fontSize: "12px",
    fontFamily: "Poppins",
    ":valid": {
      border: "1px solid #22c55e",
    },
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#D9D9D9",
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    color: "transparent",
    display: "none",
  }),
  valueContainer: (provided) => ({
    ...provided,
    fontSize: "12px",
    fontFamily: "Poppins",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#f3f4f6",
    color: "#000000",
    fontSize: "12px",
    fontFamily: "Poppins",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#000000",
    fontSize: "12px",
    fontFamily: "Poppins",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#1f293",
    ":hover": {
      backgroundColor: "#E1341E",
      color: "white",
    },
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    padding: "5px",
    fontSize: "12px",
    fontFamily: "Poppins",
  }),
};

export const customStylesMiminalist: AppSharedSelectStyles = {
  option: (provided, state) => {
    const disabledStyle = state.isDisabled ? disabledOptionRowColors() : null;
    return {
      ...provided,
      color: disabledStyle
        ? disabledStyle.color
        : defaultOptionTextColor(state),
      backgroundColor: disabledStyle
        ? disabledStyle.backgroundColor
        : defaultOptionRowBackground(state, {
            light: "#e1f0ff",
            dark: "#1e3a5f",
          }),
      cursor: state.isDisabled ? "not-allowed" : "default",
    };
  },
  control: (provided) => ({
    ...provided,
    background: "#f3f4f6",
    borderRadius: 25,
    paddingTop: 5,
    paddingBottom: 5,
    padddingLeft: 15,
    fontSize: "14px",
    fontFamily: "Poppins",
    ":valid": {
      border: "2px solid #22c55e",
    },
    borderWidth: 0,
    flexDirection: "row-reverse",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    display: "none",
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: "none",
    paddingLeft: 15,
  }),
  valueContainer: (provided) => ({
    ...provided,
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#f3f4f6",
    color: "#000000",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#000000",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#1f293",
    ":hover": {
      backgroundColor: "#E1341E",
      color: "white",
    },
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    padding: "10px",
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
  placeholder: (provided) => ({
    ...provided,
    marginLeft: 15,
  }),
};

export const scorcardSearchStyle: AppSharedSelectStyles = {
  option: (provided, state) => {
    const disabledStyle = state.isDisabled ? disabledOptionRowColors() : null;
    return {
      ...provided,
      color: disabledStyle
        ? disabledStyle.color
        : defaultOptionTextColor(state),
      backgroundColor: disabledStyle
        ? disabledStyle.backgroundColor
        : state.isSelected
          ? isAppDarkMode()
            ? "#27272a"
            : "#f5f5f5"
          : isAppDarkMode()
            ? "#18181b"
            : "#ffffff",
      cursor: state.isDisabled ? "not-allowed" : "default",
    };
  },
  control: (provided) => ({
    ...provided,
    border: "0px solid #e5e7eb",
    borderRadius: 30,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 5,
    paddingRight: 5,
    backgroundColor: "#F8F8F8",
    fontSize: "14px",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    display: "none",
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: "none",
  }),
  valueContainer: (provided) => ({
    ...provided,
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#f3f4f6",
    color: "#000000",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#000000",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#1f293",
    ":hover": {
      backgroundColor: "#E1341E",
      color: "white",
    },
  }),
  noOptionsMessage: (provided) => ({
    ...provided,
    padding: "10px",
  }),
};

export const datetimeLocalFromUtcIso = (date: string | DateTime): string => {
  const dt = typeof date === "string" ? DateTime.fromISO(date) : date;

  if (!dt.isValid) {
    return "";
  }

  // Interpret the instant in the local system zone and format it
  return dt.setZone("system").toLocaleString(DateTime.DATETIME_MED);
};

export const datetimeStringWithOffset = (
  date: Date | string | DateTime,
): string => {
  let dt: DateTime;

  if (date instanceof Date) {
    dt = DateTime.fromJSDate(date);
  } else if (typeof date === "string") {
    dt = DateTime.fromISO(date);
  } else {
    dt = date;
  }

  if (!dt.isValid) {
    return "";
  }

  const withZone = dt.setZone("system");
  const iso = withZone.toISO(); // string | null

  // Fallback for the (rare) null case
  return iso ?? withZone.toJSDate().toISOString();
};

export function toJsDateOrNull(value: unknown): Date | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  // Already a JS Date
  if (value instanceof Date) {
    return value;
  }

  // Luxon DateTime
  if (value instanceof DateTime) {
    return value.isValid ? value.toJSDate() : undefined;
  }

  // ISO string / date string / timestamp as string
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }

  // Anything else: we don't know how to convert safely
  return undefined;
}

export const isDateTimeAfterJsDate = (
  dateTime: DateTime | null | undefined,
  jsDate: { getTime: () => number },
): boolean => {
  if (!dateTime || !dateTime.isValid) return false;
  return dateTime.toMillis() > jsDate.getTime();
};

// Overloads
export function datetimeStringStartOfDay(value: string): string;
export function datetimeStringStartOfDay(value: Date): Date;
export function datetimeStringStartOfDay(value: DateTime): DateTime;

export function datetimeStringStartOfDay(
  value: string | Date | DateTime,
): string | Date | DateTime {
  const dt =
    value instanceof Date
      ? DateTime.fromJSDate(value)
      : typeof value === "string"
        ? DateTime.fromISO(value)
        : value;

  const adjusted = dt.startOf("day").plus({ minutes: 1 });

  // --- type-based return ---
  if (typeof value === "string")
    return adjusted.toLocaleString(DateTime.DATETIME_MED); // <-- string output
  if (value instanceof Date) return adjusted.toJSDate(); // <-- Date output
  return adjusted; // <-- DateTime output
}

// Overloads
export function datetimeStringEndOfDay(value: string): string;
export function datetimeStringEndOfDay(value: Date): Date;
export function datetimeStringEndOfDay(value: DateTime): DateTime;

export function datetimeStringEndOfDay(
  value: string | Date | DateTime,
): string | Date | DateTime {
  const dt =
    value instanceof Date
      ? DateTime.fromJSDate(value)
      : typeof value === "string"
        ? DateTime.fromISO(value)
        : value;

  const adjusted = dt.endOf("day");

  if (typeof value === "string")
    return adjusted.toLocaleString(DateTime.DATETIME_MED);
  if (value instanceof Date) return adjusted.toJSDate();
  return adjusted;
}

export const appendLocalTimezoneToIso = (
  date: string | DateTime,
): string | null => {
  const dt = typeof date === "string" ? DateTime.fromISO(date) : date;

  if (!dt.isValid) {
    return null;
  }

  return dt.setZone("system").toISO();
};

export const useSidebarCollapsed = (): boolean => {
  const isDesktop = useMediaQuery(BreakPoints.MD);
  const sidebarOpen = useSelector(
    (state: RootState) => state.nav?.collapseSidebar?.isOpen,
  );

  return isDesktop ? sidebarOpen : false;
};

export function useToggleDashboardQueue() {
  const dispatch = useAppDispatch();
  return useCallback(
    (value: boolean) => {
      setLocalStorageValue(
        LocalStorageItems.DASHBOARDQUEUE,
        JSON.stringify(value),
      );
      dispatch(queueAction({ showOnDashboard: value }));
    },
    [dispatch],
  );
}

export const filterTime = (date: { getTime: () => number }) => {
  const isPastTime = new Date().getTime() > date.getTime();
  return !isPastTime;
};

export const disableBackgroundScroll = () => {
  if (typeof window != "undefined" && window.document) {
    document.body.style.overflow = "hidden";
  }
};

export const enableBackgroundScroll = () => {
  document.body.style.overflow = "unset";
};

export function enforceMinAndMaxValues(e: HTMLInputElement) {
  if (e.value !== "") {
    if (parseInt(e.value) < parseInt(e.min)) {
      e.value = e.min;
    }
    if (parseInt(e.value) > parseInt(e.max)) {
      e.value = e.max;
    }
  }
}

export type ConnectionStatus = boolean | undefined | null;

export function getSimulatorConnectionStyles(
  connected: ConnectionStatus,
  styleType: "inline",
): CSSProperties;

export function getSimulatorConnectionStyles(
  connected: ConnectionStatus,
  styleType: "className",
): string;

export function getSimulatorConnectionStyles(
  connected: ConnectionStatus,
  styleType?: "inline" | "className",
): string | CSSProperties;

/**
 * Utility function to get border styling based on connection status
 * Can return either CSS properties for inline styling or a class name string for className
 */
export function getSimulatorConnectionStyles(
  connected: ConnectionStatus,
  styleType: "inline" | "className" = "inline",
): string | CSSProperties {
  // For inline styles
  if (styleType === "inline") {
    // Check if dark mode is active by looking at the HTML element's classes
    const isDarkMode =
      typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark");

    // Choose appropriate colors based on connection status and dark mode
    let borderColor;

    if (connected === true) {
      // todo: this needs to be changed to look nice
      borderColor = isDarkMode ? "rgb(22, 163, 74)" : "rgb(34, 197, 94)"; // Green
    } else if (connected === false) {
      borderColor = isDarkMode ? "rgb(185, 28, 28)" : "rgb(239, 68, 68)"; // Red
    } else {
      borderColor = isDarkMode ? "rgb(63, 63, 70)" : "rgb(229, 231, 235)"; // Gray
    }

    return {
      borderColor,
      borderWidth: connected === false ? "2px" : "1px",
      borderStyle: "solid",
      transition:
        "border-color 0.5s ease-in-out, border-width 0.5s ease-in-out",
    } as CSSProperties;
  }

  // For className
  let connectionClass =
    "border border-gray-200 dark:border-zinc-900 transition-all duration-500 ease-in-out";

  if (connected === true) {
    connectionClass =
      "border border-green-500 dark:border-green-600 transition-all duration-500 ease-in-out";
  } else if (connected === false) {
    connectionClass =
      "border-2 border-red-500 dark:border-red-600 transition-all duration-500 ease-in-out";
  }

  return connectionClass;
}

function formatRange(start: number, end: number): string {
  if (start === end) {
    return start.toString();
  } else {
    return `${start}→${end}`;
  }
}

export function formatGolfHoles(holes: number[]): string {
  // Handle empty array
  if (holes.length === 0) return "No holes selected";

  // Sort the holes to ensure proper processing
  const sortedHoles = [...holes].sort((a, b) => a - b);

  // Check for special cases
  if (
    sortedHoles.length === 18 &&
    sortedHoles.every((hole, index) => hole === index + 1)
  ) {
    return "All 18";
  }

  if (
    sortedHoles.length === 9 &&
    sortedHoles.every((hole, index) => hole === index + 1)
  ) {
    return "Front 9";
  }

  if (
    sortedHoles.length === 9 &&
    sortedHoles.every((hole, index) => hole === index + 10)
  ) {
    return "Back 9";
  }

  if (sortedHoles.length === 1) {
    return `Hole ${sortedHoles[0]}`;
  }

  // For all other cases, create range representation
  const ranges: string[] = [];
  let rangeStart = sortedHoles[0];
  let rangeEnd = sortedHoles[0];

  for (let i = 1; i < sortedHoles.length; i++) {
    if (sortedHoles[i] === (rangeEnd ?? 0) + 1) {
      // Extend current range
      rangeEnd = sortedHoles[i];
    } else {
      // End current range and start new one
      ranges.push(formatRange(rangeStart ?? 0, rangeEnd ?? 0));
      rangeStart = sortedHoles[i];
      rangeEnd = sortedHoles[i];
    }
  }

  // Add the final range
  ranges.push(formatRange(rangeStart ?? 0, rangeEnd ?? 0));

  return `[${sortedHoles.length}]: ${ranges.join(", ")}`;
}

// Helper for creating className strings
export function cx(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
